import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { FEATURE_REGISTRY }                 from '../features/featureRegistry';
import { hasFeatureAccess, hasModule as hasModuleAccess } from '../features/moduleAccess';
import { mergeWithDefaults }               from '../constants/settingsDefaults';
import { getFunctions, httpsCallable } from 'firebase/functions';
import auth from '../firebase/auth';
import db from '../firebase/firestore';
import app from '../firebase/config';
import { getSessionId } from '../hooks/useTrackEvent';
import { clearTrustData } from '../components/admin/AdminLogin';

const AuthContext = createContext(null);

// Lizenzfelder aus dem User-Dokument (alle optional — Bestandsuser haben sie nicht).
function extractLicenseData(userData) {
  return {
    licensePlan:              userData.licensePlan ?? null,
    selectedModules:          userData.selectedModules ?? [],
    purchasedModules:         userData.purchasedModules ?? [],
    previouslyActiveModules:  userData.previouslyActiveModules ?? [],
    moduleRemovals:           userData.moduleRemovals ?? {},
    moduleTrials:             userData.moduleTrials ?? {},
    isTester:                 userData.isTester === true,
    testerModules:            userData.testerModules ?? null,
    creditLimit:              userData.creditLimit ?? null,
    creditsUsedCurrentPeriod: userData.creditsUsedCurrentPeriod ?? 0,
    billingStatus:            userData.billingStatus ?? null,
    currentPeriodStart:       userData.currentPeriodStart ?? null,
    currentPeriodEnd:         userData.currentPeriodEnd ?? null,
    billingInterval:          userData.billing?.billingInterval ?? null,
    cancelAtPeriodEnd:        userData.billing?.cancelAtPeriodEnd === true,
    hasPaddleCustomer:        !!userData.billing?.paddleCustomerId,
    manualOverride:           userData.billing?.manualOverride === true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser]                       = useState(null);
  const [claims, setClaims]                   = useState({});
  const [loading, setLoading]                 = useState(true);
  const [enabledFeatures, setEnabledFeatures] = useState([]);
  const [licenseData, setLicenseData]         = useState({});
  const [featureAreas, setFeatureAreas]       = useState({});
  const [termsAccepted, setTermsAccepted]     = useState(false);
  const [userStatus, setUserStatus]           = useState(null);
  const [aiModels, setAiModels]                         = useState(['brain', 'orb']);
  const [preferredAIModel, setPreferredAIModel]         = useState('orb');
  const [assistantPersonality, setAssistantPersonality] = useState('atlas');
  const [userSettings, setUserSettings]                 = useState(() => mergeWithDefaults(null));
  const [elevenLabsVoiceId,  setElevenLabsVoiceId]       = useState('');
  const [globalVoiceId,      setGlobalVoiceId]           = useState('');
  const [jokesEnabled,       setJokesEnabled]            = useState(true);
  const [privacyLayerActive, setPrivacyLayerActive]      = useState(false);
  const [phoneNumber,        setPhoneNumber]             = useState('');
  const [avatarUrl,          setAvatarUrl]               = useState('');
  const [activeImpersonation, setActiveImpersonation]    = useState(null);

  const userDocUnsubRef = useRef(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Vorherigen User-Dokument-Listener aufräumen
      if (userDocUnsubRef.current) {
        userDocUnsubRef.current();
        userDocUnsubRef.current = null;
      }

      setLoading(true);

      if (firebaseUser) {
        // forceRefresh: true damit Custom Claims (z.B. admin) sofort sichtbar sind
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        setClaims(tokenResult.claims);
        setUser(firebaseUser);

        // login-Event + lastLoginAt einmal pro Browser-Session tracken
        if (!sessionStorage.getItem('_ts_login_tracked')) {
          sessionStorage.setItem('_ts_login_tracked', '1');
          try {
            const p = addDoc(collection(db, 'usage_events'), {
              userId:    firebaseUser.uid,
              eventType: 'login',
              sessionId: getSessionId(),
              timestamp: serverTimestamp(),
            });
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } catch {}
          // lastLoginAt im User-Dokument persistieren (non-fatal)
          try {
            httpsCallable(getFunctions(app, 'europe-west1'), 'recordLastLogin')()
              .catch(() => {});
          } catch {}
        }

        // Echtzeit-Listener für User-Dokument (reagiert auf Telegram-Freigaben sofort)
        let firstSnapshot = true;
        userDocUnsubRef.current = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          (snap) => {
            const userData = snap.data() || {};
            setEnabledFeatures(userData.enabledFeatures ?? []);
            setLicenseData(extractLicenseData(userData));
            setFeatureAreas(userData.featureAreas ?? {});
            setUserStatus(userData.status ?? null);
            setTermsAccepted(!!userData.termsAcceptedAt);
            const models = userData.aiModels ?? ['brain', 'orb'];
            setAiModels(models);
            const preferred = userData.preferredAIModel ?? 'orb';
            setPreferredAIModel(models.includes(preferred) ? preferred : models[0] ?? 'orb');
            setAssistantPersonality(userData.assistantPersonality ?? 'atlas');
            setUserSettings(mergeWithDefaults(userData.userSettings ?? null));
            setElevenLabsVoiceId(userData.elevenLabsVoiceId ?? '');
            setPhoneNumber(userData.phoneNumber ?? '');
            setAvatarUrl(userData.avatarUrl ?? '');
            setActiveImpersonation(userData.activeImpersonation ?? null);
            if (firstSnapshot) {
              firstSnapshot = false;
              // Globale Standard-Stimme einmalig laden
              getDoc(doc(db, 'system_config', 'voice_settings'))
                .then((snap) => { if (snap.exists()) setGlobalVoiceId(snap.data()?.defaultVoiceId ?? ''); })
                .catch(() => {});
              getDoc(doc(db, 'system_config', 'coach_features'))
                .then((snap) => { if (snap.exists()) setJokesEnabled(snap.data()?.jokesEnabled !== false); })
                .catch(() => {});
              // Privacy Layer M2: Datenschutz-Badge nur zeigen, wenn Shadow Mode aktiv ist
              getDoc(doc(db, 'system_config', 'privacy_layer'))
                .then((snap) => { if (snap.exists()) setPrivacyLayerActive(snap.data()?.shadowModeEnabled === true); })
                .catch(() => {});
              setLoading(false);
            }
          },
          () => { setLoading(false); } // Fehler-Fallback: App nicht blockieren
        );
      } else {
        setUser(null);
        setClaims({});
        setEnabledFeatures([]);
        setLicenseData({});
        setFeatureAreas({});
        setUserStatus(null);
        setTermsAccepted(false);
        setAiModels(['brain', 'orb']);
        setPreferredAIModel('orb');
        setAssistantPersonality('atlas');
        setUserSettings(mergeWithDefaults(null));
        setElevenLabsVoiceId('');
        setGlobalVoiceId('');
        setPhoneNumber('');
        setAvatarUrl('');
        setActiveImpersonation(null);
        setLoading(false);
      }
    });

    return () => {
      if (typeof unsubscribeAuth === 'function') unsubscribeAuth();
      if (userDocUnsubRef.current) userDocUnsubRef.current();
    };
  }, []);

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    if (activeImpersonation) {
      try {
        await httpsCallable(getFunctions(app, 'europe-west1'), 'endImpersonation')();
      } catch {}
    }
    clearTrustData(user?.email);
    await setPersistence(auth, browserLocalPersistence);
    await signOut(auth);
  }

  // Beendet eine laufende Impersonation und meldet den Admin wieder an.
  async function endImpersonation() {
    const { data } = await httpsCallable(getFunctions(app, 'europe-west1'), 'endImpersonation')();
    await setPersistence(auth, browserLocalPersistence);
    await signInWithCustomToken(auth, data.customToken);
  }

  // Refresh token + claims after role changes (e.g. after setUserRole)
  async function refreshClaims() {
    if (!user) return;
    const tokenResult = await user.getIdTokenResult(true);
    setClaims(tokenResult.claims);
  }

  // Refresh enabledFeatures + status (onSnapshot handled this automatically,
  // but keeping for manual refresh cases)
  async function refreshFeatures() {
    if (!user) return;
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    const userData = userSnap.data() || {};
    setEnabledFeatures(userData.enabledFeatures ?? []);
    setLicenseData(extractLicenseData(userData));
    setUserStatus(userData.status ?? null);
  }

  // Dual-Read: Feature aktiv über Legacy-enabledFeatures ODER über ein aktives Modul.
  function hasFeature(featureId) {
    return hasFeatureAccess({ ...licenseData, enabledFeatures, userSettings }, featureId);
  }

  // Modul-Prüfung (inkl. Legacy-Fallback für Bestandsuser ohne Lizenzfelder).
  function hasModule(moduleKey) {
    return hasModuleAccess({ ...licenseData, enabledFeatures }, moduleKey);
  }

  // Gibt den effektiven Bereich zurück: nutzer-Override → Registry-Default → 'work'
  function getFeatureArea(featureId) {
    if (featureAreas[featureId]) return featureAreas[featureId];
    const reg = FEATURE_REGISTRY.find((f) => f.id === featureId);
    return reg?.area || 'work';
  }

  // Nutzungsbedingungen akzeptieren
  async function acceptTerms() {
    try {
      await httpsCallable(getFunctions(app, 'europe-west1'), 'acceptTerms')();
      setTermsAccepted(true);
    } catch (err) {
      console.error('[acceptTerms] Fehler:', err);
      throw err;
    }
  }

  // Resolve TOTP MFA challenge during sign-in
  async function resolveTotpMfa(multiFactorError, totpCode) {
    const resolver = getMultiFactorResolver(auth, multiFactorError);
    const hint = resolver.hints.find(
      (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
    );
    if (!hint) throw new Error('Kein TOTP-Faktor registriert.');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, totpCode);
    return resolver.resolveSignIn(assertion);
  }

  const isAdmin = claims?.admin === true;

  const value = {
    user,
    claims,
    loading,
    isAdmin,
    enabledFeatures,
    licenseData,
    featureAreas,
    hasFeature,
    hasModule,
    getFeatureArea,
    userStatus,
    termsAccepted,
    acceptTerms,
    login,
    logout,
    refreshClaims,
    refreshFeatures,
    resolveTotpMfa,
    aiModels,
    preferredAIModel,
    assistantPersonality,
    userSettings,
    elevenLabsVoiceId,
    globalVoiceId,
    // activeVoiceId: per-User-ID hat Vorrang, sonst globale Standard-Stimme
    activeVoiceId: elevenLabsVoiceId || globalVoiceId || '',
    jokesEnabled,
    privacyLayerActive,
    phoneNumber,
    avatarUrl,
    activeImpersonation,
    endImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden.');
  return ctx;
}
